import { createTimerHook, type TimerState, type TimerHookReturn } from "../../../shared/hooks/create-timer-hook";
import { getTimerSettings } from "../../settings/hooks/use-timer-settings";
import { playSound } from "../../settings/utils/sound-presets";
import { POMODORO_MODES, type PomodoroMode } from "../../settings/types/timer-settings";

export type PomodoroState = TimerState<PomodoroMode>;

export interface UsePomodoroReturn extends TimerHookReturn<PomodoroMode> {
  readonly state: PomodoroState;
}

function getDuration(mode: PomodoroMode): number {
  const s = getTimerSettings().pomodoro;
  if (mode === "work") return s.workDuration * 60;
  if (mode === "longBreak") return s.longBreakDuration * 60;
  return s.shortBreakDuration * 60;
}

export const usePomodoro = createTimerHook<PomodoroMode>({
  storageKey: "palnia_pomodoro",
  serverKey: "pomodoro",
  legacyCounterKey: "sessionsCompleted",
  validModes: POMODORO_MODES,
  defaultMode: "work",
  getDefaultDuration: () => getTimerSettings().pomodoro.workDuration * 60,

  getTransition: (prev) => {
    const s = getTimerSettings().pomodoro;
    if (prev.mode === "work") {
      const nextCounter = prev.counter + 1;
      const nextMode: PomodoroMode = nextCounter % s.sessionsBeforeLongBreak === 0 ? "longBreak" : "break";
      return { mode: nextMode, timeLeft: getDuration(nextMode), counter: nextCounter };
    }
    return { mode: "work", timeLeft: getDuration("work"), counter: prev.counter };
  },

  getAutoStart: (fromSkip) => {
    const s = getTimerSettings().pomodoro;
    return fromSkip ? s.autoStartOnSkip : s.autoStartNext;
  },

  onTransitionSound: (endedMode) => {
    const s = getTimerSettings().pomodoro;
    playSound(endedMode === "work" ? s.workEndSound : s.breakEndSound);
  },

  getNotificationMessage: (endedMode) => {
    if (endedMode === "work") return "Session de travail terminée — c'est l'heure de la pause !";
    return "Pause terminée — prêt pour une nouvelle session ?";
  },
});
